import { useCallback, useEffect, useRef } from 'react';

const ICON_FALLBACK = 28;
const INDENT = 28;
const BUTTON_CHROME = 36;
const BUTTON_GAP = 8;
const COUNT_GAP = 8;
const HEADER_ICON = 28;
const SAFETY = 16;

let measureCanvas;

function textWidth(text, font) {
  const value = text == null ? '' : String(text);
  if (!value) return 0;
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return value.length * 8;
  ctx.font = font || '14px sans-serif';
  return ctx.measureText(value).width;
}

function readMetrics(rootEl) {
  const sample = rootEl?.querySelector('.ag-cell, .ag-header-cell-text');
  const font = sample ? getComputedStyle(sample).font : '14px sans-serif';
  const cell = rootEl?.querySelector('.ag-cell');
  let pad = 48;
  if (cell) {
    const style = getComputedStyle(cell);
    pad = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) + SAFETY;
  }
  const icon = rootEl?.querySelector('.ag-group-contracted, .ag-group-expanded');
  const iconWidth = icon && icon.offsetWidth ? icon.offsetWidth + 8 : ICON_FALLBACK;
  const collectBtn = rootEl?.querySelector('.ag-group-value button');
  return { font, pad, iconWidth, collectBtnWidth: collectBtn ? collectBtn.offsetWidth : 0 };
}

function formatValue(def, value, data) {
  if (typeof def.valueFormatter === 'function') {
    try {
      const formatted = def.valueFormatter({ value, data, colDef: def });
      if (formatted != null) return String(formatted);
    } catch (err) {
      /* use the raw value */
    }
  }
  if (value == null) return '';
  return String(value);
}

function leafLabel(def, data) {
  const field = def.field;
  if (!field || !data) return '';
  if (field === 'suggestion_count') {
    const n = Number(data.suggestion_count || 0);
    if (n === 1) return '1 match';
    if (n > 1) return `${n} ambiguous`;
    return 'none';
  }
  if (field === 'source_type') {
    if (data.source_type === 'toll' && data.source_id) {
      return `toll …${String(data.source_id).slice(-6)}`;
    }
    return data.source_type || '';
  }
  if (field === 'amount' && !def.valueFormatter) {
    return `$${Number(data.amount || 0).toFixed(2)}`;
  }
  return formatValue(def, data[field], data);
}

function isButtonColumn(def) {
  if (typeof def.cellRenderer !== 'function') return false;
  const name = def.headerName ?? '';
  return name === 'Actions' || name === '';
}

function headerLabel(api, column) {
  const def = column.getColDef();
  const name = api.getDisplayNameForColumn?.(column, 'header') || def.headerName || def.field || '';
  if (typeof def.aggFunc === 'string' && name && !String(name).includes(`${def.aggFunc}(`)) {
    return `${def.aggFunc}(${name})`;
  }
  return name;
}

function buttonFloor(def, font, pad) {
  const retry = textWidth('Collecting…', font) + textWidth('Waive', font);
  const assign = textWidth('Assign', font) + textWidth('Dismiss', font);
  return Math.ceil(Math.max(retry, assign) + BUTTON_CHROME * 2 + BUTTON_GAP + pad);
}

function groupFields(api) {
  return (api.getColumns() || [])
    .filter((column) => column.getColDef().rowGroup)
    .map((column) => column.getColDef().field)
    .filter(Boolean);
}

function isAutoGroup(column) {
  const def = column.getColDef();
  return def.showRowGroup === true || column.getColId() === 'ag-Grid-AutoColumn';
}

function collectButtonWidth(font, renderedWidth) {
  const label = Math.max(textWidth('Collect unpaid', font), textWidth('Collecting…', font));
  return Math.max(label + BUTTON_CHROME, renderedWidth || 0);
}

function groupedWidth(rows, fields, level, metrics, showCollectButton) {
  if (!fields.length || !rows.length) return 0;
  const field = fields[0];
  const buckets = new Map();
  rows.forEach((row) => {
    const key = row[field] == null ? '' : String(row[field]);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  });
  let max = 0;
  const buttonWidth = collectButtonWidth(metrics.font, metrics.collectBtnWidth);
  buckets.forEach((kids, key) => {
    let width =
      textWidth(key, metrics.font) +
      textWidth(`(${kids.length})`, metrics.font) +
      COUNT_GAP +
      metrics.iconWidth +
      level * INDENT +
      metrics.pad;
    if (
      showCollectButton &&
      field === 'driver_name' &&
      kids.some((row) => row.status === 'open' || row.status === 'give_up')
    ) {
      width += buttonWidth + BUTTON_GAP;
    }
    max = Math.max(max, width, groupedWidth(kids, fields.slice(1), level + 1, metrics, showCollectButton));
  });
  return max;
}

function columnContentWidth(api, column, rows, metrics, showCollectButton) {
  const def = column.getColDef();
  const font = metrics.font;
  let headerExtra = 0;
  if (def.filter) headerExtra += HEADER_ICON;
  if (def.sortable !== false) headerExtra += 16;
  let max = textWidth(headerLabel(api, column), font) + metrics.pad + headerExtra;

  if (isAutoGroup(column)) {
    return Math.max(max, groupedWidth(rows, groupFields(api), 0, metrics, showCollectButton));
  }
  if (isButtonColumn(def)) {
    return Math.max(max, buttonFloor(def, font, metrics.pad));
  }

  rows.forEach((row) => {
    const label = leafLabel(def, row);
    const width = textWidth(label, font) + metrics.pad;
    if (width > max) max = width;
  });

  if (def.aggFunc === 'sum' && def.field) {
    let total = 0;
    rows.forEach((row) => {
      total += Number(row[def.field] || 0);
    });
    const totalWidth = textWidth(formatValue(def, total, null), font) + metrics.pad;
    if (totalWidth > max) max = totalWidth;
    const topField = groupFields(api)[0];
    if (topField) {
      const sums = new Map();
      rows.forEach((row) => {
        const key = row[topField];
        sums.set(key, (sums.get(key) || 0) + Number(row[def.field] || 0));
      });
      sums.forEach((sum) => {
        const width = textWidth(formatValue(def, sum, null), font) + metrics.pad;
        if (width > max) max = width;
      });
    }
  }

  return max;
}

export function fitColumnsToContent(api, rootEl, options = {}) {
  if (!api || api.isDestroyed?.()) return;
  const columns = (api.getColumns() || []).filter((column) => column.isVisible());
  if (!columns.length) return;
  const rows = [];
  api.forEachNode((node) => {
    if (node.data && !node.group) rows.push(node.data);
  });
  const metrics = readMetrics(rootEl);
  const showCollectButton = Boolean(options.showCollectButton);
  const widths = columns.map((column) => {
    const needed = columnContentWidth(api, column, rows, metrics, showCollectButton);
    const min = column.getMinWidth?.() || 0;
    return { key: column.getColId(), newWidth: Math.ceil(Math.max(needed, min)) };
  });
  api.setColumnWidths(widths);
  growClippedColumns(api, rootEl);
}

function growClippedColumns(api, rootEl) {
  if (!rootEl) return;
  const bumps = new Map();
  rootEl.querySelectorAll('.ag-cell, .ag-header-cell').forEach((el) => {
    const colId = el.getAttribute('col-id');
    if (!colId) return;
    let extra = 0;
    const button = el.querySelector('button');
    if (button) {
      const buttonRect = button.getBoundingClientRect();
      const cellRect = el.getBoundingClientRect();
      extra = Math.max(extra, buttonRect.right - cellRect.right, buttonRect.bottom - cellRect.bottom);
    }
    const text = el.querySelector('.ag-header-cell-text');
    if (text) {
      const textRect = text.getBoundingClientRect();
      const cellRect = el.getBoundingClientRect();
      extra = Math.max(extra, textRect.right - (cellRect.right - 8));
    }
    if (extra > 2) bumps.set(colId, Math.max(bumps.get(colId) || 0, extra));
  });
  if (!bumps.size) return;
  const updates = [];
  bumps.forEach((extra, colId) => {
    const column = api.getColumn(colId);
    if (!column) return;
    updates.push({
      key: colId,
      newWidth: Math.ceil(column.getActualWidth() + extra + SAFETY),
    });
  });
  if (updates.length) api.setColumnWidths(updates);
}

export function useFitGridColumns(showCollectButton = false) {
  const apiRef = useRef(null);
  const rootRef = useRef(null);
  const showRef = useRef(showCollectButton);
  showRef.current = showCollectButton;

  const fit = useCallback(() => {
    const api = apiRef.current;
    if (!api || api.isDestroyed?.()) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const live = apiRef.current;
        if (!live || live.isDestroyed?.()) return;
        fitColumnsToContent(live, rootRef.current, { showCollectButton: showRef.current });
      });
    });
  }, []);

  useEffect(() => {
    fit();
  }, [fit, showCollectButton]);

  return {
    rootRef,
    onGridReady: (event) => {
      apiRef.current = event.api;
    },
    onFirstDataRendered: fit,
    onRowDataUpdated: fit,
  };
}
